<template>
  <div>
    <b-modal
      id="share-proof-video-from-fb"
      ref="modal"
      modal-class="share-video-proof-modal"
      content-class="share-video-proof-modal__content"
      body-class="share-video-proof-modal__body"
      hide-header
      hide-footer
      centered
      no-close-on-backdrop
      @show="onShow"
      @hide="onHide"
    >
      <div class="share-video-proof-modal__panel">
        <div class="share-video-proof-modal__header">
          <h2 class="share-video-proof-modal__title">
            Share {{ isGifProof ? 'GIF' : 'Video' }} Proof
          </h2>
          <button
            type="button"
            class="share-video-proof-modal__close"
            aria-label="Close"
            @click="close()"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        <div
          v-if="isSharing"
          class="share-video-proof-modal__loading"
        >
          <b-spinner small />
          <span>Loading proof…</span>
        </div>

        <template v-else>
          <div class="share-video-proof-modal__options">
            <label class="share-video-proof-modal__check">
              <input
                v-model="notifyActivity"
                type="checkbox"
              >
              <span
                class="share-video-proof-modal__check-box"
                aria-hidden="true"
              >
                <svg
                  class="share-video-proof-modal__check-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              </span>
              Notify me of proof activity
            </label>

            <div class="share-video-proof-modal__notify-others">
              <label class="share-video-proof-modal__check">
                <input
                  v-model="notifyOthers"
                  type="checkbox"
                >
                <span
                  class="share-video-proof-modal__check-box"
                  aria-hidden="true"
                >
                  <svg
                    class="share-video-proof-modal__check-icon"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </span>
                Notify others…
              </label>

              <div
                v-if="notifyOthers"
                class="share-video-proof-modal__notify-others-body"
              >
                <b-form-tags
                  v-model="notifyOthersEmails"
                  class="share-video-proof-modal__tags"
                  input-id="share-video-proof-emails"
                  placeholder="Add email addresses"
                  invalid-tag-text="Invalid email(s)"
                  remove-on-delete
                  add-on-change
                  :add-on-blur="true"
                  separator=" ,;"
                  :tag-validator="isValidEmail"
                />

                <div
                  v-if="visibleRecentRecipients.length"
                  class="share-video-proof-modal__recents"
                >
                  <div
                    v-for="email in visibleRecentRecipients"
                    :key="email"
                    class="share-video-proof-modal__recent"
                  >
                    <button
                      type="button"
                      class="share-video-proof-modal__recent-add"
                      :title="`Add ${email}`"
                      @click="addRecentToEmails(email)"
                    >
                      {{ email }}
                    </button>
                    <button
                      type="button"
                      class="share-video-proof-modal__recent-remove"
                      aria-label="Remove from recent recipients"
                      @click="removeRecent(email)"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <label class="share-video-proof-modal__check">
              <input
                v-model="requestApproval"
                type="checkbox"
              >
              <span
                class="share-video-proof-modal__check-box"
                aria-hidden="true"
              >
                <svg
                  class="share-video-proof-modal__check-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              </span>
              Request approval
            </label>
          </div>

          <div
            v-if="baseUrl"
            class="share-video-proof-modal__link"
          >
            <label class="share-video-proof-modal__link-label">Share a link</label>
            <div class="share-video-proof-modal__link-row">
              <input
                ref="copyTarget"
                class="share-video-proof-modal__link-input"
                :value="proofUrl"
                readonly
                @click="copyLink"
              >
              <button
                type="button"
                class="share-video-proof-modal__icon-btn"
                title="Copy link"
                aria-label="Copy link"
                @click="copyLink"
              >
                <i class="fa fa-clipboard" />
              </button>
              <a
                class="share-video-proof-modal__icon-btn"
                :href="proofUrl"
                target="_blank"
                rel="noopener noreferrer"
                title="Open link"
                aria-label="Open link"
              >
                <i class="fa fa-external-link-alt" />
              </a>
            </div>
            <p
              v-if="copied"
              class="share-video-proof-modal__copied"
            >
              Link copied to clipboard!
            </p>
          </div>

          <div class="share-video-proof-modal__footer">
            <button
              type="button"
              class="share-video-proof-modal__btn-cancel"
              @click="close()"
            >
              Cancel
            </button>
            <button
              type="button"
              class="share-video-proof-modal__btn-create"
              :disabled="isSharing"
              @click="createProof"
            >
              <b-spinner
                v-if="isSharing"
                small
              />
              Create Proof
            </button>
          </div>
        </template>
      </div>
    </b-modal>
  </div>
</template>

<script>
import mixin from './mixin.js';
import { createNamespacedHelpers } from 'vuex';

const { mapGetters } = createNamespacedHelpers('fileBrowser');

const RECENT_EMAILS_KEY = 'share-video-proof-recent-emails';
const MAX_RECENT_EMAILS = 6;

export default {
  name: 'ShareProofVideoModal',
  mixins: [mixin],
  data: () => ({
    notifyActivity: true,
    notifyOthers: true,
    notifyOthersEmails: [],
    requestApproval: true,
    isSharing: false,
    baseUrl: null,
    copied: null,
    recentRecipients: [],
  }),
  computed: {
    ...mapGetters(['selectedItems']),
    isGifProof() {
      return this.selectedItems[0]?.type === 'gif';
    },
    proofUrl() {
      if (!this.baseUrl) return null;
      if (this.notifyActivity || this.requestApproval ||
        (this.notifyOthers && this.notifyOthersEmails.length)) {
        const request = {
          id: this.getUserId(),
          approve: !!this.requestApproval,
          notify: !!this.notifyActivity,
          notify_other: !!(this.notifyOthers && this.notifyOthersEmails.length),
          email_other: this.notifyOthers && this.notifyOthersEmails.length
            ? this.notifyOthersEmails.join(',')
            : false,
        };
        return `${this.baseUrl}/${btoa(JSON.stringify(request))}`;
      }
      return this.baseUrl;
    },
    visibleRecentRecipients() {
      const selected = new Set(
        this.notifyOthersEmails.map((email) => String(email).trim().toLowerCase()),
      );
      return this.recentRecipients
        .filter((email) => !selected.has(String(email).trim().toLowerCase()))
        .slice(0, MAX_RECENT_EMAILS);
    },
  },
  methods: {
    async init() {
      this.baseUrl = null;
      this.notifyActivity = true;
      this.notifyOthers = true;
      this.notifyOthersEmails = [];
      this.requestApproval = true;
      this.copied = null;
      this.recentRecipients = this.loadRecentRecipients();
      this.isSharing = true;
      this.$refs.modal.show();
      await this.fetchProof(true);
    },
    close(silent = false) {
      this._silentClose = silent;
      this.$refs.modal?.hide();
    },
    onShow() {
      document.body.classList.add('share-video-proof-modal-open');
    },
    onHide() {
      document.body.classList.remove('share-video-proof-modal-open');
      this.closeHandler();
    },
    getUserId() {
      return $('meta[name="userId"]').attr('content');
    },
    isValidEmail(value) {
      const trimmed = String(value || '').trim();
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    },
    loadRecentRecipients() {
      try {
        const stored = JSON.parse(localStorage.getItem(RECENT_EMAILS_KEY) || '[]');
        if (!Array.isArray(stored)) return [];
        return stored
          .map((email) => String(email).trim())
          .filter((email) => this.isValidEmail(email))
          .slice(0, MAX_RECENT_EMAILS);
      } catch (e) {
        return [];
      }
    },
    persistRecentRecipients(emails) {
      localStorage.setItem(
        RECENT_EMAILS_KEY,
        JSON.stringify(emails.slice(0, MAX_RECENT_EMAILS)),
      );
    },
    saveRecipientsFromForm() {
      const fromForm = this.notifyOthersEmails
        .map((email) => String(email).trim())
        .filter((email) => this.isValidEmail(email));
      if (!fromForm.length) return;

      const seen = new Set();
      const merged = [];
      for (const email of [...fromForm, ...this.loadRecentRecipients()]) {
        const key = email.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push(email);
      }
      this.persistRecentRecipients(merged);
      this.recentRecipients = merged.slice(0, MAX_RECENT_EMAILS);
    },
    addRecentToEmails(email) {
      const trimmed = String(email).trim();
      if (!trimmed || !this.isValidEmail(trimmed)) return;

      const exists = this.notifyOthersEmails.some(
        (item) => String(item).trim().toLowerCase() === trimmed.toLowerCase(),
      );
      if (!exists) {
        this.notifyOthersEmails = [...this.notifyOthersEmails, trimmed];
      }

      this.recentRecipients = this.recentRecipients.filter(
        (item) => String(item).trim().toLowerCase() !== trimmed.toLowerCase(),
      );
      this.notifyOthers = true;
    },
    removeRecent(email) {
      const key = String(email).trim().toLowerCase();
      this.recentRecipients = this.recentRecipients.filter(
        (item) => String(item).trim().toLowerCase() !== key,
      );
      this.persistRecentRecipients(
        this.loadRecentRecipients().filter(
          (item) => String(item).trim().toLowerCase() !== key,
        ),
      );
    },
    async fetchProof(lookupOnly = false) {
      this.isSharing = true;
      const file = this.selectedItems[0];
      if (!file) {
        this.isSharing = false;
        return;
      }
      try {
        const payload = {
          file_video_url: file.path,
          name: file.name,
        };
        if (lookupOnly) {
          payload.lookup_only = true;
        } else {
          payload.share_settings = {
            id: Number(this.getUserId()) || null,
            notify: !!this.notifyActivity,
            approve: !!this.requestApproval,
            notify_other: !!(this.notifyOthers && this.notifyOthersEmails.length),
            email_other: this.notifyOthers && this.notifyOthersEmails.length
              ? this.notifyOthersEmails.join(',')
              : null,
          };
        }
        const { data } = await axios.post('/video/proof/share', payload);
        this.baseUrl = data.share_url;
      } catch (e) {
        toastr.error('Something went wrong while loading proof');
      } finally {
        this.isSharing = false;
      }
    },
    async createProof() {
      if (this.notifyOthers && this.notifyOthersEmails.length) {
        this.saveRecipientsFromForm();
      }
      await this.fetchProof();
    },
    async copyLink() {
      const input = this.$refs.copyTarget;
      if (input?.select) input.select();
      try {
        await navigator.clipboard.writeText(this.proofUrl);
        this.copied = true;
        setTimeout(() => { this.copied = null; }, 1400);
      } catch (error) {
        console.error('Failed to copy link:', error);
      }
    },
  },
};
</script>
